import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { clientApi, ScenarioDetail } from '@/api/client'
import { qk } from '@/api/queryKeys'
import Topbar from '@/components/Topbar'
import UniverseTabs from '@/components/UniverseTabs'
import ErrorBox from '@/components/ErrorBox'
import Skeleton from '@/components/Skeleton'
import { timeAgo } from '@/utils/format'

function statusPill(status?: string) {
  const s = String(status || '').toLowerCase()
  if (s === 'realized') return 'pill ok'
  if (s === 'disproven') return 'pill bad'
  if (s === 'archived') return 'pill'
  return 'pill'
}

export default function Scenarios() {
  const [filter, setFilter] = useState<'all' | 'hypothetical' | 'active' | 'realized' | 'disproven' | 'archived'>('all')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [search, setSearch] = useState('')

  const scenariosQuery = useQuery({
    queryKey: qk.scenarios.list({ includeArchived }),
    queryFn: () => clientApi.listScenarios({ includeArchived }),
    staleTime: 30_000,
  })
  const scenarios = scenariosQuery.data?.scenarios || []

  const counts = useMemo(() => {
    const by = { all: scenarios.length, hypothetical: 0, active: 0, realized: 0, disproven: 0, archived: 0 }
    for (const s of scenarios) {
      const k = String(s.status || 'hypothetical') as keyof typeof by
      if (k in by) by[k] += 1
    }
    return by
  }, [scenarios])

  const filtered = useMemo(() => {
    let rows = scenarios
    if (filter !== 'all') rows = rows.filter((s) => (s.status || 'hypothetical') === filter)
    const q = search.trim().toLowerCase()
    if (q) {
      rows = rows.filter((s) => {
        if (s.label?.toLowerCase().includes(q)) return true
        if (s.description?.toLowerCase().includes(q)) return true
        return (s.assumptions || []).some((a) => (a.text || '').toLowerCase().includes(q))
      })
    }
    return rows
  }, [scenarios, filter, search])

  return (
    <div className="container">
      <Topbar subtitle="Scenarios" />
      <UniverseTabs />

      {scenariosQuery.error && <ErrorBox error={scenariosQuery.error} />}

      {scenariosQuery.isLoading && (
        <div className="grid" style={{ marginTop: 16 }}>
          <Skeleton variant="card" count={3} />
        </div>
      )}

      {!scenariosQuery.isLoading && (
        <div className="grid">
          <div className="card">
            <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <h2 style={{ margin: 0 }}>Scenarios</h2>
                <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                  Reusable sets of hard assumptions that condition forecasts. Each scenario has its own history
                  of conditional probabilities across every question it's attached to.
                </div>
              </div>
              <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                <label className="row" style={{ gap: 6, alignItems: 'center', fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={includeArchived}
                    onChange={(e) => setIncludeArchived(e.target.checked)}
                  />
                  Show archived
                </label>
              </div>
            </div>

            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12, marginBottom: 10 }}>
              {(['all', 'hypothetical', 'active', 'realized', 'disproven', 'archived'] as const).map((k) => (
                <button
                  key={k}
                  className={`btn ${filter === k ? 'primary' : ''}`}
                  type="button"
                  onClick={() => setFilter(k)}
                >
                  {k === 'all' ? 'All' : k.charAt(0).toUpperCase() + k.slice(1)} ({counts[k] ?? 0})
                </button>
              ))}
              <input
                type="search"
                placeholder="Search label, description, assumptions…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ flex: 1, minWidth: 200, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)' }}
              />
            </div>

            <div className="stack" style={{ gap: 0 }}>
              {filtered.map((s) => (
                <ScenarioRow key={s.scenario_id} scenario={s} />
              ))}
              {filtered.length === 0 && (
                <div className="muted" style={{ fontSize: 13 }}>
                  {scenarios.length === 0
                    ? 'No scenarios yet. Save a scenario from the Ask flow to start tracking it here.'
                    : 'No scenarios match the current filters.'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ScenarioRow({ scenario }: { scenario: ScenarioDetail }) {
  const assumptionPreview = (scenario.assumptions || [])
    .slice(0, 3)
    .map((a) => a.text)
    .filter(Boolean)
  return (
    <Link to={`/universe/scenarios/${scenario.scenario_id}`} className="signal-row" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, lineHeight: 1.4 }}>{scenario.label}</div>
          {scenario.description && (
            <div className="muted" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>{scenario.description}</div>
          )}
          {assumptionPreview.length > 0 && (
            <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 12, lineHeight: 1.5 }}>
              {assumptionPreview.map((t, i) => (
                <li key={i} className="muted">{t}</li>
              ))}
              {(scenario.assumptions || []).length > 3 && (
                <li className="muted" style={{ fontStyle: 'italic' }}>+{scenario.assumptions.length - 3} more</li>
              )}
            </ul>
          )}
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <span className={statusPill(scenario.status)}>{scenario.status || 'hypothetical'}</span>
          {typeof scenario.question_count === 'number' && (
            <span className="pill">
              {scenario.question_count} question{scenario.question_count === 1 ? '' : 's'}
            </span>
          )}
          {scenario.updated_at && (
            <span className="pill">updated {timeAgo(scenario.updated_at)}</span>
          )}
        </div>
      </div>
    </Link>
  )
}
